import {Component} from "react";
import PropTypes from "prop-types";
import Glob from "../../libs/Glob.mjs";

/**
 * @private
 * @typedef TextLabelProps
 * @type {Object}
 * @property {string} value Label mask name
 * @property {Record<string,string|number>} [params] Optional parameters for label mask
 * @property {string} [lang] Optional forced language
 */

/**
 * @desc Multilanguage labels
 * @abstract Generate a text label from a set of multilingual labels, with or without passed values for a "mask".
 * @abstract The labels, unless a language is declared, update themselves automatically on language change.
 * @version 1.2
 * @author [Marco Balestra](https://altersoftware.it)
 * @type {Component}
 * @property {string} value Label mask name
 * @property {Record<string,string|number>} [params] Optional parameters for label mask
 * @property {string} [lang] Optional forced language
 */
export const TextLabel = class extends Component {
	static propTypes = {
		/**
		 * Label mask name
		 * @type {string}
		 */
		value: PropTypes.string.isRequired,
		/**
		 * Optional parameters for label mask
		 * @type {Record<string,string|number>}
		 */
		params: PropTypes.object,
		/**
		 * @desc Optional forced language.
		 * @desc Defaults to current language and changes dynamically.
		 * @abstract Provide this value only if you want that the label sticks on a specific language.
		 * @type {string}
		 */
		lang: PropTypes.string
	}

	/** @param {TextLabelProps} props */
	constructor(props) {
		super(props);
		this.state = { lang: this.props.lang ?? Glob.getLanguage() };
		this.uid = Glob.generateUid("TextLabel");
	}
	componentDidMount() {
		if ( ! this.props.lang ) {
			Glob.register("Prefs", ()=>{
				const l = Glob.getLanguage();
				if ( l && l !== this.state.lang) this.setState({ lang: l });
			}, this.uid);
		}
	}
	componentWillUnmount() {
		if ( ! this.props.lang ) {
			Glob.unregister("Prefs", this.uid);
		}
	}
	render() {
		return Glob.label(this.props.value, this.props.params, this.state.lang);
	}
}

export default TextLabel;
